import { defineComponent, onMounted, computed } from 'vue';
import { storeToRefs } from 'pinia';
import {
  currencyConverterFormRefs,
  usePaymentMethodCurrencyConverterListStore,
} from '@/store/ps-management/global-currency-converter-list.store';
import { usePermissionsStore } from '@/store/permissions.store';
import { PS_MANAGEMENT_PERMISSIONS } from '@/models/app-internal/permissions.model';
import { ConverterKey, PaymentMethodCurrencyConverterItem } from '@/models/ps-management/currency-converter.model';
import { currencyConverterSettingsFormRules } from '@/hooks/ps-management/currency-converter-validation-rules';
import { InfoFilled, Close } from '@element-plus/icons-vue';
import { ElNotification, FormInstance } from 'element-plus';
import FallbackImage from '@/components/fallback-image/fallback-image.vue';

const icons = {
  InfoFilled,
  Close,
};

export default defineComponent({
  name: 'CurrencyConverterSettings',
  components: {
    FallbackImage,
  },
  emits: ['updatePaymentMethodList'],
  setup(_, { emit }) {
    const paymentMethodsCurrencyConverterStore = usePaymentMethodCurrencyConverterListStore();
    const {
      isCurrencyConverterSettingsSidebarVisible,
      convertableCurrencyList,
      availableConvertToCurrencyListCounter,
      paymentMethodCurrencyConverterList,
      paymentMethodConverterListCount,
      isPaymentMethodCurrencyConverterListLoading,
      currentPaymentMethodId,
      currentPaymentMethod,
      isCurrencyConverterFormChanged,
    } = storeToRefs(paymentMethodsCurrencyConverterStore);
    const {
      closeCurrencyConverterSettings,
      getPaymentMethodCurrencyConverterList,
      getConvertableCurrencyListRequest,
      updatePaymentMethodCurrencyConverterList,
      addCurrencyConverter,
      removeCurrencyConverter,
    } = paymentMethodsCurrencyConverterStore;

    const { psManagementPermissions } = storeToRefs(usePermissionsStore());

    onMounted(() => {
      if (!currentPaymentMethodId.value) {
        return;
      }

      getPaymentMethodCurrencyConverterList();
      getConvertableCurrencyListRequest();
    });

    const isAddCurrencyConverterButtonVisible = computed<boolean>(() => {
      return (
        psManagementPermissions.value[
          PS_MANAGEMENT_PERMISSIONS.SYNC_PAYMENT_METHOD_CURRENCY_CONVERTER_LIST
        ] &&
        availableConvertToCurrencyListCounter.value > paymentMethodConverterListCount.value
      );
    });

    const availableCurrency = (currentIndex: number, key: ConverterKey): string[] => {
      const selectedInOtherCards = paymentMethodCurrencyConverterList.value
        .filter((_, index) => index !== currentIndex)
        .flatMap((converter: PaymentMethodCurrencyConverterItem) => converter[key]);

      const sourceList =
        key === 'currencyFromList'
          ? convertableCurrencyList.value.currencyFromList
          : convertableCurrencyList.value.currencyToList;

      return sourceList.filter(
        (currency: string) => !selectedInOtherCards.includes(currency),
      );
    };

    const saveCurrencyConverterSettings = async (): Promise<void> => {
      const allFormValidatioresults = await Promise.all(
        currencyConverterFormRefs.value.map((form: FormInstance) => form?.validate()),
      );

      if (allFormValidatioresults.includes(false)) {
        ElNotification({
          title: 'Error',
          message: 'Please properly fill all required fields.',
          type: 'error',
        });

        return;
      }

      updatePaymentMethodCurrencyConverterList()
        .then(() => {
          emit('updatePaymentMethodList');
          closeCurrencyConverterSettings();
        });
    };

    return {
      icons,
      isCurrencyConverterSettingsSidebarVisible,
      isPaymentMethodCurrencyConverterListLoading,
      closeCurrencyConverterSettings,
      currentPaymentMethod,
      isAddCurrencyConverterButtonVisible,
      paymentMethodCurrencyConverterList,
      convertableCurrencyList,
      addCurrencyConverter,
      removeCurrencyConverter,
      currencyConverterSettingsFormRules,
      updatePaymentMethodCurrencyConverterList,
      availableCurrency,
      saveCurrencyConverterSettings,
      currencyConverterFormRefs,
      isCurrencyConverterFormChanged,
    };
  },
});
